/**
 * Usage API Client
 *
 * This client provides usage and quota data for the frontend-plugin-usage.
 * Uses tRPC to communicate with the billing backend.
 */

import type {
  Plan,
  QuotaStatus,
  Subscription,
  UsageAggregation,
  UsageAggregationOptions,
  UsageApiClient,
  UsageHistoryEntry,
  UsageHistoryOptions,
  UsageNode,
} from '@xhubio-saas/core-frontend/plugin-usage'
import { useMemo } from 'react'
import { trpc } from '@/trpc'

/**
 * Response type from tRPC billing.getSubscription
 */
interface SubscriptionResponse {
  id: string
  planId: string
  status: string
  currentPeriodStart: string
  currentPeriodEnd: string
  trialEnd?: string
}

/**
 * Response type from tRPC billing.getQuotaStatus
 */
interface QuotaStatusResponse {
  eventType: string
  hasQuota: boolean
  limit?: number | 'unlimited'
  currentUsage: number
  remaining?: number
  periodType?: string
  periodStart?: string
  periodEnd?: string
  softLimitReached?: boolean
}

type UsageTrpcUtils = ReturnType<typeof trpc.useUtils>

/**
 * Max event types per getBatchQuotaStatus request.
 *
 * getBatchQuotaStatus is a tRPC query → sent as GET with the input encoded in
 * the URL. The client caps URLs at maxURLLength (2048). With a large entitlement
 * catalog the full event-type list overflows that cap and the httpBatchLink
 * rejects the dispatch with "Input is too big for a single dispatch". Chunking
 * keeps each request's URL bounded; results are concatenated in order.
 */
const QUOTA_BATCH_CHUNK_SIZE = 20

async function fetchBatchQuotaChunked(
  utils: UsageTrpcUtils,
  eventTypes: string[]
): Promise<QuotaStatusResponse[]> {
  const out: QuotaStatusResponse[] = []
  for (let i = 0; i < eventTypes.length; i += QUOTA_BATCH_CHUNK_SIZE) {
    const chunk = eventTypes.slice(i, i + QUOTA_BATCH_CHUNK_SIZE)
    const part = (await utils.billing.getBatchQuotaStatus.fetch({
      eventTypes: chunk,
    })) as QuotaStatusResponse[]
    out.push(...part)
  }
  return out
}

/**
 * Resolve quota statuses for the given event types.
 *
 * Prefers billing.getAllQuotaStatus (no input → one request, URL never grows
 * with the catalog) and filters to the requested subset. Falls back to the
 * chunked getBatchQuotaStatus when the endpoint is unavailable (older backend).
 */
async function fetchQuotaStatuses(
  utils: UsageTrpcUtils,
  eventTypes: string[]
): Promise<QuotaStatusResponse[]> {
  try {
    // biome-ignore lint/suspicious/noExplicitAny: untyped (deprecated) trpc client; getAllQuotaStatus resolved by path
    const all = (await (utils.billing as any).getAllQuotaStatus.fetch()) as QuotaStatusResponse[]
    const wanted = new Set(eventTypes)
    return all.filter((q) => wanted.has(q.eventType))
  } catch {
    return fetchBatchQuotaChunked(utils, eventTypes)
  }
}

/**
 * Creates a Usage API client that uses tRPC to communicate with the backend.
 *
 * This function should be called within a React component that has access
 * to the tRPC context (inside TRPCProvider).
 *
 * @returns UsageApiClient implementation using tRPC
 */
export function useUsageClient(): UsageApiClient {
  const utils = trpc.useUtils()

  return useMemo(
    () => ({
      async getSubscription(): Promise<Subscription | null> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getSubscription.invalidate()

        const response =
          (await utils.billing.getSubscription.fetch()) as SubscriptionResponse | null

        if (!response) {
          return null
        }

        // Convert ISO strings back to Date objects
        return {
          id: response.id,
          planId: response.planId,
          status: response.status as Subscription['status'],
          currentPeriodStart: new Date(response.currentPeriodStart),
          currentPeriodEnd: new Date(response.currentPeriodEnd),
          trialEnd: response.trialEnd ? new Date(response.trialEnd) : undefined,
        }
      },

      async getPlan(): Promise<Plan | null> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getPlan.invalidate()
        return utils.billing.getPlan.fetch()
      },

      async getQuotaStatus(eventType: string): Promise<QuotaStatus> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getQuotaStatus.invalidate({ eventType })
        const response = (await utils.billing.getQuotaStatus.fetch({
          eventType,
        })) as QuotaStatusResponse

        // Convert ISO strings back to Date objects
        return {
          eventType: response.eventType,
          hasQuota: response.hasQuota,
          limit: response.limit,
          currentUsage: response.currentUsage,
          remaining: response.remaining,
          periodType: response.periodType as QuotaStatus['periodType'],
          periodStart: response.periodStart ? new Date(response.periodStart) : undefined,
          periodEnd: response.periodEnd ? new Date(response.periodEnd) : undefined,
          softLimitReached: response.softLimitReached,
        }
      },

      async getBatchQuotaStatus(eventTypes: string[]): Promise<QuotaStatus[]> {
        await utils.billing.getBatchQuotaStatus.invalidate()
        const responses = await fetchQuotaStatuses(utils, eventTypes)

        return responses.map((response) => ({
          eventType: response.eventType,
          hasQuota: response.hasQuota,
          limit: response.limit,
          currentUsage: response.currentUsage,
          remaining: response.remaining,
          periodType: response.periodType as QuotaStatus['periodType'],
          periodStart: response.periodStart ? new Date(response.periodStart) : undefined,
          periodEnd: response.periodEnd ? new Date(response.periodEnd) : undefined,
          softLimitReached: response.softLimitReached,
        }))
      },

      async getConfiguredEventTypes(): Promise<string[]> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getConfiguredEventTypes.invalidate()
        return utils.billing.getConfiguredEventTypes.fetch()
      },

      async getQuotaTypes(): Promise<
        Array<{ id: string; name: string; unit: string; displayUnit: string }>
      > {
        await utils.billing.getQuotaTypes.invalidate()
        return utils.billing.getQuotaTypes.fetch()
      },

      /**
       * Get hierarchical usage aggregation from quota data.
       * Prefers quota types (quota:* prefixed event types) with metadata from billing config.
       * Falls back to showing all event types if no quota:* types are configured.
       */
      async getUsageAggregation(_options: UsageAggregationOptions): Promise<UsageAggregation> {
        // Invalidate billing caches to force fresh fetch
        await utils.billing.getConfiguredEventTypes.invalidate()
        await utils.billing.getQuotaStatus.invalidate()

        // Get quota type definitions (optional - enhances display with names/units)
        let quotaTypes: Array<{
          id: string
          name: string
          unit: string
          displayUnit: string
        }> = []
        try {
          await utils.billing.getQuotaTypes.invalidate()
          quotaTypes = (await utils.billing.getQuotaTypes.fetch()) as typeof quotaTypes
        } catch {
          // getQuotaTypes endpoint may not be available yet - continue without metadata
        }

        // Build a lookup map: quota:<id> -> QuotaType metadata
        const quotaTypeMap = new Map(quotaTypes.map((qt) => [`quota:${qt.id}`, qt]))

        // Get all configured event types
        const eventTypes = (await utils.billing.getConfiguredEventTypes.fetch()) as string[]

        // If quota:* event types exist, show only those (with metadata).
        // Otherwise fall back to all event types.
        const quotaEventTypes = eventTypes.filter((et) => et.startsWith('quota:'))
        const displayEventTypes = quotaEventTypes.length > 0 ? quotaEventTypes : eventTypes

        // If no event types configured, return empty aggregation
        if (displayEventTypes.length === 0) {
          return { total: 0, byHierarchy: [] }
        }

        // Fetch all quota statuses in a single batch request to avoid
        // exhausting the DB connection pool with parallel individual calls
        let quotas: QuotaStatusResponse[]
        try {
          await utils.billing.getBatchQuotaStatus.invalidate()
          quotas = await fetchQuotaStatuses(utils, displayEventTypes)
        } catch {
          // Fallback to individual calls if batch endpoint not available
          const quotaPromises = displayEventTypes.map(async (eventType) => {
            const response = (await utils.billing.getQuotaStatus.fetch({
              eventType,
            })) as QuotaStatusResponse
            return response
          })
          quotas = await Promise.all(quotaPromises)
        }

        // Transform into usage nodes, using quota type metadata for labels when available
        const nodes: UsageNode[] = quotas
          .filter((q) => q.hasQuota)
          .map((quota) => {
            const qtMeta = quotaTypeMap.get(quota.eventType)
            return {
              pattern: quota.eventType,
              label: qtMeta?.name ?? quota.eventType,
              count: quota.currentUsage,
              limit: typeof quota.limit === 'number' ? quota.limit : undefined,
              displayUnit: qtMeta?.displayUnit,
            }
          })

        // Calculate total usage
        const total = nodes.reduce((sum, node) => sum + node.count, 0)

        return {
          total,
          byHierarchy: nodes,
        }
      },

      /**
       * Get usage history for charts.
       * Calls the backend getUsageHistory endpoint.
       */
      async getUsageHistory(options: UsageHistoryOptions): Promise<UsageHistoryEntry[]> {
        // Invalidate cache to force fresh fetch
        await utils.billing.getUsageHistory.invalidate()
        const response = (await utils.billing.getUsageHistory.fetch({
          startDate: options.startDate.toISOString(),
          endDate: options.endDate.toISOString(),
          granularity: options.granularity,
          pattern: options.pattern,
        })) as Array<{ date: string; pattern: string; count: number }>

        // Convert ISO strings back to Date objects
        return response.map((entry) => ({
          date: new Date(entry.date),
          pattern: entry.pattern,
          count: entry.count,
        }))
      },
    }),
    [utils]
  )
}
